import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
    InvitationSource,
    BAInvitationQueryParam
} from '../core-data-model/Assessment.model';
import { UserProfileSettingsService } from '../system-services';
import { InitDataService } from '../system-services/InitData/InitData.service';
import { Logger } from '../system-services/Logger/Logger.service';
import { TranslationService } from '../translations/Translation.service';
import { SkipOrLaunchBaService } from './skip-or-launch-ba.service';

@Component({
    selector: `skip-or-launch-ba`,
    template: `
        <div
            class="empty-profile"
            piTesting="ba-experience-empty-profile"
            [ngStyle]="{ opacity: isThirdPartyUser ? '.5' : '1' }"
        >
            <div class="body-container">
                <img
                    src="/Assets/Profile-EmptyState.svg"
                    width="100px"
                    style="align-self: flex-start"
                />
                <div class="layoutVertical" style="padding: 0 20px">
                    <div class="title-md">
                        {{ getHeaderKeyName() }}
                    </div>
                    <div class="copy-sm" style="padding: 15px 0;">
                        {{ TR.key('skipOrLaunchBA.intro') }}
                    </div>
                    <div class="button-container">
                        <button
                            class="button button-primary button-md"
                            (click)="openNewTab($event)"
                            [ngClass]="{
                                'button--loading': isCreatingBA
                            }"
                            piTesting="ba-experience-take-assessment-button"
                            [disabled]="isThirdPartyUser"
                        >
                            {{ TR.key('skipOrLaunchBA.assessmentButton') }}
                        </button>
                        @if (!hideSkipAssessmentText) {
                            <span class="skip-container">
                                <span class="color-neutral-700">
                                    {{ TR.key('skipOrLaunchBA.or') }}
                                </span>
                                <span
                                    class="skip-text color-primary"
                                    (click)="skipBAStep()"
                                    piTesting="ba-experience-skip"
                                >
                                    {{ TR.key('skipOrLaunchBA.skip') }}
                                </span>
                            </span>
                        }
                    </div>
                </div>
            </div>
            <div class="new-tab-text color-neutral-700">
                {{ TR.key('skipOrLaunchBA.footer') }}
            </div>
        </div>
    `,
    styles: [
        `
            .empty-profile {
                width: 500px;
                border-radius: 6px;
                background-color: #f2f2f2;
                border: 1px solid #d1d1d1;
                padding: 25px 20px;
                box-sizing: border-box;
            }
            .skip-text {
                font-size: 14px;
                cursor: pointer;
                text-decoration: underline;
                margin-left: 5px;
            }
            .button-container {
                align-items: center;
            }
            .new-tab-text {
                font-style: italic;
                font-size: 14px;
                line-height: 21px;
            }
            .body-container {
                display: flex;
                justify-items: flex-start;
                flex-direction: row;
                margin-bottom: 30px;
            }
            .skip-container {
                margin-left: 10px;
            }
            .button[disabled] {
                opacity: 0.3;
            }
        `
    ],
    standalone: false
})
export class SkipOrLaunchBaComponent implements OnInit {
    isCreatingBA = false;
    isThirdPartyUser: boolean;

    @Input()
    invitationSource: InvitationSource;

    @Input()
    hideSkipAssessmentText = false;

    @Input()
    invitationQueryParam: BAInvitationQueryParam;

    @Input()
    globalNavContext = false;

    @Output()
    assessmentCreated: EventEmitter<{
        id: string;
        url: string;
    }> = new EventEmitter();

    @Output()
    skipBA: EventEmitter<void> = new EventEmitter();

    constructor(
        public TR: TranslationService,
        private skipOrLaunchBaService: SkipOrLaunchBaService,
        private userProfileSettingsService: UserProfileSettingsService,
        private logger: Logger,
        public initData: InitDataService
    ) {}

    ngOnInit(): void {
        this.isThirdPartyUser =
            !this.userProfileSettingsService.isUserLoggedInDefaultCompany();
    }

    getHeaderKeyName() {
        return this.globalNavContext
            ? this.TR.key('skipOrLaunchBA.globalNavHeader')
            : this.TR.key('skipOrLaunchBA.header');
    }

    async openNewTab(e: Event) {
        e.stopPropagation(); // To prevent clickOutside directive on the my profile tooltip from firing
        e.preventDefault();

        this.isCreatingBA = true;
        const assessmentId = await this.createPersonAndGenerateBA();
        const assessmentUrl = await this.skipOrLaunchBaService.getBALink(
            assessmentId,
            this.invitationQueryParam
        );

        this.logger.logInfo(
            'SkipOrLaunchBA - BA experience take assessment button clicked',
            {
                userId: this.initData.user.userId,
                email: this.initData.user.email,
                companyId: this.initData.user.companyId
            }
        );

        const baWindow = window.open(assessmentUrl, '_blank');
        // Needed to communicate across tabs to allow a button on the assessment side to close the assessment through this window.
        // javascript cannot be used to close windows themselves, only windows that are opened by another window and only then when the other window does it.
        const processCrossWindowMessage = function (
            event: MessageEvent<string>
        ) {
            if (assessmentUrl.startsWith(event.origin)) {
                baWindow.close();
                window.removeEventListener(
                    'message',
                    processCrossWindowMessage
                );
            }
        };

        window.addEventListener('message', processCrossWindowMessage);
        this.assessmentCreated.emit({ id: assessmentId, url: assessmentUrl });
        this.isCreatingBA = false;
    }

    createPersonAndGenerateBA() {
        return this.skipOrLaunchBaService.createPersonAndGenerateBA(
            this.invitationSource,
            this.invitationQueryParam
        );
    }

    skipBAStep() {
        this.logger.logInfo('SkipOrLaunchBA - BA experience skipped', {
            userId: this.initData.user.userId,
            email: this.initData.user.email,
            companyId: this.initData.user.companyId
        });
        this.skipBA.emit();
    }
}
